import React, { useState, CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMutation } from "react-query";
import { recruitingAPI } from "../services/recruitingApi";

interface JobCandidateFinderProps {
  jobId: string | number;
  onCreateApplication?: (candidateId: number) => void;
}

interface FindOptions {
  page?: number;
  pageSize?: number;
  [key: string]: unknown;
}

interface CandidateInfo {
  id: number;
  name?: string;
  email?: string;
  mainSkill?: string;
  seniority?: string;
  location?: string;
}

interface CandidateMatch {
  candidate: CandidateInfo;
  matchScore: number;
  rating?: string;
}

interface FindResults {
  candidates?: CandidateMatch[];
  totalFound: number;
  totalPages: number;
  page: number;
  pageSize: number;
}

const JobCandidateFinder: React.FC<JobCandidateFinderProps> = ({
  jobId,
  onCreateApplication,
}) => {
  const { t } = useTranslation("recruiting");
  const navigate = useNavigate();
  const [results, setResults] = useState<FindResults | null>(null);
  const [isFinding, setIsFinding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const findMutation = useMutation<FindResults, Error, FindOptions>(
    async (options) => {
      const result = (await recruitingAPI.findCandidatesForJob(jobId, {
        ...options,
        page: currentPage,
        pageSize: 10,
      })) as { data: FindResults };
      return result.data;
    },
    {
      onSuccess: (result) => {
        setResults(result);
        if (result.page) {
          setCurrentPage(result.page);
        }
        setIsFinding(false);
        setError(null);
      },
      onError: (err) => {
        setIsFinding(false);
        setError(err.message || t("jobFinder.searchFailed"));
      },
    },
  );

  const handleFind = () => {
    setIsFinding(true);
    setError(null);
    setCurrentPage(1);
    findMutation.mutate({});
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    setIsFinding(true);
    setError(null);
    findMutation.mutate({ page: newPage, pageSize: 10 });
  };

  const handleViewCandidate = (candidateId: number) => {
    navigate(`/candidates/${candidateId}`);
  };

  const handleCreateApplication = (candidateId: number) => {
    if (onCreateApplication) {
      onCreateApplication(candidateId);
    }
  };

  const getRatingColor = (rating?: string): string => {
    switch (rating?.toLowerCase()) {
      case "excellent":
        return "#10b981";
      case "good":
        return "#3b82f6";
      case "fair":
        return "#f59e0b";
      case "poor":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  };

  const tableHeaderStyle: CSSProperties = {
    padding: "0.75rem",
    textAlign: "left",
    fontWeight: 600,
  };
  const tableCellStyle: CSSProperties = { padding: "0.75rem" };

  return (
    <div className="job-candidate-finder">
      <div className="finder-header">
        <h3>{t("jobFinder.title")}</h3>
        <button
          className="btn-primary"
          onClick={handleFind}
          disabled={isFinding}
        >
          {isFinding ? t("jobFinder.finding") : t("jobFinder.findButton")}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {results && (
        <div className="finder-results">
          <div className="results-header">
            <p>
              {t("jobFinder.foundMatching", { count: results.totalFound })}
              {results.totalPages > 1 && (
                <span>
                  {" "}
                  (
                  {t("jobFinder.pageInfo", {
                    page: results.page,
                    total: results.totalPages,
                  })}
                  )
                </span>
              )}
            </p>
          </div>

          {results.candidates && results.candidates.length > 0 ? (
            <>
              <table
                className="candidates-table"
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginTop: "1rem",
                }}
              >
                <thead>
                  <tr
                    style={{
                      borderBottom: "2px solid #e5e7eb",
                      backgroundColor: "#f9fafb",
                    }}
                  >
                    <th style={tableHeaderStyle}>
                      {t("jobFinder.columns.rank")}
                    </th>
                    <th style={tableHeaderStyle}>
                      {t("jobFinder.columns.name")}
                    </th>
                    <th style={tableHeaderStyle}>
                      {t("jobFinder.columns.email")}
                    </th>
                    <th style={tableHeaderStyle}>
                      {t("jobFinder.columns.matchScore")}
                    </th>
                    <th style={tableHeaderStyle}>
                      {t("jobFinder.columns.rating")}
                    </th>
                    <th style={tableHeaderStyle}>
                      {t("jobFinder.columns.mainSkill")}
                    </th>
                    <th style={tableHeaderStyle}>
                      {t("jobFinder.columns.seniority")}
                    </th>
                    <th style={tableHeaderStyle}>
                      {t("jobFinder.columns.location")}
                    </th>
                    <th style={tableHeaderStyle}>
                      {t("jobFinder.columns.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {results.candidates.map((match, index) => {
                    const rank =
                      (results.page - 1) * results.pageSize + index + 1;
                    return (
                      <tr
                        key={match.candidate.id}
                        style={{
                          borderBottom: "1px solid #e5e7eb",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#f9fafb")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "transparent")
                        }
                      >
                        <td style={{ ...tableCellStyle, fontWeight: 500 }}>
                          {rank}
                        </td>
                        <td style={tableCellStyle}>
                          {match.candidate.name || t("jobFinder.na")}
                        </td>
                        <td style={{ ...tableCellStyle, color: "#6b7280" }}>
                          {match.candidate.email || t("jobFinder.noEmail")}
                        </td>
                        <td style={tableCellStyle}>
                          <span
                            style={{
                              fontWeight: 600,
                              color: getRatingColor(match.rating),
                            }}
                          >
                            {Math.round(match.matchScore)}%
                          </span>
                        </td>
                        <td style={tableCellStyle}>
                          <span
                            style={{
                              padding: "0.25rem 0.5rem",
                              borderRadius: "0.25rem",
                              fontSize: "0.875rem",
                              backgroundColor: getRatingColor(match.rating),
                              color: "white",
                              textTransform: "capitalize",
                            }}
                          >
                            {match.rating || t("jobFinder.na")}
                          </span>
                        </td>
                        <td style={tableCellStyle}>
                          {match.candidate.mainSkill || t("jobFinder.na")}
                        </td>
                        <td style={tableCellStyle}>
                          {match.candidate.seniority || t("jobFinder.na")}
                        </td>
                        <td style={tableCellStyle}>
                          {match.candidate.location || t("jobFinder.na")}
                        </td>
                        <td style={tableCellStyle}>
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button
                              className="btn btn-outline"
                              style={{
                                padding: "0.25rem 0.5rem",
                                fontSize: "0.875rem",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewCandidate(match.candidate.id);
                              }}
                            >
                              {t("jobFinder.view")}
                            </button>
                            <button
                              className="btn-primary"
                              style={{
                                padding: "0.25rem 0.5rem",
                                fontSize: "0.875rem",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCreateApplication(match.candidate.id);
                              }}
                            >
                              {t("jobFinder.attach")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              {results.totalPages > 1 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "1rem",
                    marginTop: "1.5rem",
                  }}
                >
                  <button
                    className="btn btn-secondary"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || isFinding}
                  >
                    {t("jobFinder.previous")}
                  </button>
                  <span style={{ color: "#6b7280" }}>
                    {t("jobFinder.pageInfo", {
                      page: results.page,
                      total: results.totalPages,
                    })}
                  </span>
                  <button
                    className="btn btn-secondary"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= results.totalPages || isFinding}
                  >
                    {t("jobFinder.next")}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div
              className="no-candidates"
              style={{ padding: "2rem", textAlign: "center" }}
            >
              <p>{t("jobFinder.noCandidates")}</p>
            </div>
          )}
        </div>
      )}

      {!results && !isFinding && (
        <div
          className="finder-info"
          style={{ padding: "2rem", textAlign: "center", color: "#6b7280" }}
        >
          <p>{t("jobFinder.infoText")}</p>
        </div>
      )}
    </div>
  );
};

export default JobCandidateFinder;
